// Baselayer Background Checks — staff list view at /background-checks. Filters
// (status / type / deal) live in the URL; a "Run check" button opens the
// RunCheckModal (gated on any Run* permission). When the URL carries ?dealId=,
// the modal auto-opens preselected to that deal.

import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck } from "lucide-react";
import {
  Button,
  EmptyState,
  FilterChips,
  ListCard,
  PageHeader,
  SearchField,
  SkeletonList,
  StatusBadge,
  Timestamp,
} from "@/design-system/components/ui";
import { useAuth } from "@/hooks/useAuth";
import { useBackgroundChecks } from "@/hooks/backgroundChecks/useBackgroundChecks";
import { RunCheckModal } from "@/components/backgroundChecks/RunCheckModal";
import type {
  BackgroundCheckStatus,
  BackgroundCheckType,
} from "@/api/backgroundChecks/backgroundChecksApi";
import {
  CHECK_STATUS_LABELS,
  CHECK_TYPE_LABELS,
  checkStatusTone,
} from "@/components/backgroundChecks/checkFormat";

const STATUS_CHIPS: { id: string; label: string }[] = [
  { id: "", label: "All statuses" },
  ...(
    Object.entries(CHECK_STATUS_LABELS) as [BackgroundCheckStatus, string][]
  ).map(([id, label]) => ({ id, label })),
];

const TYPE_CHIPS: { id: string; label: string }[] = [
  { id: "", label: "All types" },
  ...(
    Object.entries(CHECK_TYPE_LABELS) as [BackgroundCheckType, string][]
  ).map(([id, label]) => ({ id, label })),
];

export default function BackgroundChecksPage() {
  const auth = useAuth();
  const [params, setParams] = useSearchParams();
  const dealId = params.get("dealId") ?? "";
  const status = params.get("status") ?? "";
  const type = params.get("type") ?? "";
  const subject = params.get("subject") ?? "";
  const [modalOpen, setModalOpen] = useState(false);

  const canRun =
    auth.hasPermission("BackgroundCheck.RunKyb") ||
    auth.hasPermission("BackgroundCheck.RunConsumer") ||
    auth.hasPermission("BackgroundCheck.RunOnline") ||
    auth.hasPermission("BackgroundCheck.RunLienSearch");

  // ?dealId= deep-link auto-opens the modal preselected to that deal.
  useEffect(() => {
    if (dealId && canRun) setModalOpen(true);
  }, [dealId, canRun]);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const listQuery = useBackgroundChecks({
    dealId: dealId || undefined,
    status: status || undefined,
    type: type || undefined,
    subject: subject || undefined,
  });

  const checks = useMemo(() => listQuery.data ?? [], [listQuery.data]);
  const hasFilters = Boolean(status || type || subject);

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        title="Background Checks"
        eyebrow="Baselayer"
        subtitle="Run KYB, watchlist, and lien checks; file the rendered reports to the DMS."
        actions={
          canRun ? (
            <Button
              type="button"
              onClick={() => setModalOpen(true)}
              data-testid="bgc-run-button"
            >
              <ShieldCheck size={16} aria-hidden="true" /> Run check
            </Button>
          ) : undefined
        }
      />

      <div className="flex flex-col gap-space-3">
        <FilterChips
          chips={STATUS_CHIPS}
          activeId={status}
          onChange={(id) => setParam("status", id)}
          label="Filter by status"
        />
        <FilterChips
          chips={TYPE_CHIPS}
          activeId={type}
          onChange={(id) => setParam("type", id)}
          label="Filter by type"
        />
        <SearchField
          value={subject}
          onChange={(v) => setParam("subject", v)}
          placeholder="Search by subject…"
        />
      </div>

      {listQuery.isLoading ? (
        <SkeletonList rows={5} />
      ) : checks.length === 0 ? (
        <EmptyState
          title={hasFilters ? "No matching checks" : "No background checks yet"}
          description={
            hasFilters
              ? "No background checks match the current filters."
              : "Run a check to verify a counterparty against Baselayer."
          }
          variant={hasFilters ? "noResults" : "empty"}
          icon={ShieldCheck}
          action={
            canRun && !hasFilters
              ? { label: "Run check", onClick: () => setModalOpen(true) }
              : undefined
          }
        />
      ) : (
        <div className="flex flex-col gap-space-2">
          {checks.map((c) => (
            <ListCard
              key={c.id}
              to={`/background-checks/${c.id}`}
              title={c.subjectName}
              meta={
                <span className="flex flex-wrap items-center gap-space-2 text-caption text-gray-mid">
                  <span>{CHECK_TYPE_LABELS[c.checkType]}</span>
                  <span aria-hidden>·</span>
                  <span>{c.subjectKind}</span>
                  <span aria-hidden>·</span>
                  <Timestamp value={c.createdAt} prefix="Created" />
                </span>
              }
              status={
                <StatusBadge tone={checkStatusTone(c.status)}>
                  {CHECK_STATUS_LABELS[c.status]}
                </StatusBadge>
              }
              data-testid="bgc-list-row"
            />
          ))}
        </div>
      )}

      <RunCheckModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        dealId={dealId || null}
      />
    </div>
  );
}
